import { AvailableIcon } from 'src/components/atoms/Icon';
import { IntegrationService } from 'src/api/queries';
import { ModelStatus } from 'src/api/models';
import { Campaign } from '../components/molecules/CampaignList';

export const MODEL_STATUS_CONFIG: Record<
  ModelStatus,
  { color: string; bg: string; label: string }
> = {
  success: {
    color: 'status.text.success',
    bg: 'statusBg.success',
    label: 'Active',
  },
  running: {
    color: 'status.text.running',
    bg: 'statusBg.running',
    label: 'Running',
  },
  fail: {
    color: 'status.text.fail',
    bg: 'statusBg.fail',
    label: 'Failed',
  },
  not_started: {
    color: 'status.text.not_started',
    bg: 'statusBg.notStarted',
    label: 'Pending',
  },
};

export type ChannelStatus = 'connected' | 'failed' | 'not-connected';
export type ConnectionAction = 'edit' | 'reconnect';

export type ChannelCategory = 'media' | 'kpi';

export type ChannelDefinition = {
  id: string;
  service: IntegrationService;
  name: string;
  icon: AvailableIcon;
  placeholder?: string;
  category: ChannelCategory;
  defaultAccountName?: string;
  disabled?: boolean;
};

export type Channel = ChannelDefinition & {
  accountName: string;
  status: ChannelStatus;
  lastConnected?: string;
  selectedAccounts: string[] | null;
  campaignNameFilter?: string | null;
  actions: ConnectionAction[];
};

export const SERVICES_REQUIRING_ACCOUNT_SELECTION: IntegrationService[] = [
  'google_ads',
  'youtube',
  'dv360',
  'cm360',
  'tiktok',
  'snapchat',
  'linkedin',
  'facebook',
  'instagram',
  'pinterest',
  'twitter',
  'ga4',
];

// Services where the user must select exactly one account (radio-style
// selection in SelectAccountsModal instead of checkboxes)
export const SINGLE_ACCOUNT_SERVICES: IntegrationService[] = ['ga4'];

// Services supporting a campaign name (prefix) filter
export const SERVICES_SUPPORTING_CAMPAIGN_FILTER: IntegrationService[] = [
  'google_ads',
  'youtube',
  'facebook',
  'instagram',
];

export const mediaChannelDefinitions: ChannelDefinition[] = [
  {
    id: 'facebook',
    service: 'facebook',
    name: 'Facebook',
    icon: 'facebook',
    placeholder: 'Add your Facebook Ads manager name',
    category: 'media',
    defaultAccountName: 'Facebook Ads account',
  },
  {
    id: 'instagram',
    service: 'instagram',
    name: 'Instagram',
    icon: 'instagram',
    placeholder: 'Add your Instagram Ads account name',
    category: 'media',
    defaultAccountName: 'Instagram Ads account',
  },
  {
    id: 'google-ads',
    service: 'google_ads',
    name: 'Google ADS',
    icon: 'google-ads',
    placeholder: 'Add your Google Ads account name',
    category: 'media',
    defaultAccountName: 'Google Ads account',
  },
  {
    id: 'youtube',
    service: 'youtube',
    name: 'YouTube',
    icon: 'youtube',
    placeholder: 'Add your YouTube Ads account name',
    category: 'media',
    defaultAccountName: 'YouTube Ads account',
  },
  {
    id: 'dv360',
    service: 'dv360',
    name: 'Display & Video 360 (DV360)',
    icon: 'dv360',
    placeholder: 'Add your DV360 advertiser ID',
    category: 'media',
    defaultAccountName: 'DV360 account',
  },
  {
    id: 'cm360',
    service: 'cm360',
    name: 'Campaign Manager 360 (CM360)',
    icon: 'cv360',
    placeholder: 'Add your CM360 advertiser ID',
    category: 'media',
    defaultAccountName: 'CM360 account',
  },
  {
    id: 'tiktok',
    service: 'tiktok',
    name: 'TikTok',
    icon: 'tiktok',
    placeholder: 'Add your TikTok Ads account name',
    category: 'media',
    defaultAccountName: 'TikTok Ads account',
  },
  {
    id: 'linkedin-ads',
    service: 'linkedin',
    name: 'LinkedIn Ads',
    icon: 'linkedin',
    placeholder: 'Add your LinkedIn Ads account name',
    category: 'media',
    defaultAccountName: 'LinkedIn Ads account',
  },
  {
    id: 'snapchat',
    service: 'snapchat',
    name: 'Snapchat',
    icon: 'snapchat',
    placeholder: 'Add your Snapchat Ads account name',
    category: 'media',
    defaultAccountName: 'Snapchat Ads account',
  },
  {
    id: 'twitter',
    service: 'twitter',
    name: 'X',
    icon: 'x-logo',
    placeholder: 'Add your X Ads account name',
    category: 'media',
    defaultAccountName: 'X Ads account',
  },
  // {
  //   id: 'pinterest',
  //   service: 'pinterest',
  //   name: 'Pinterest',
  //   icon: 'pinterest',
  //   placeholder: 'Add your Pinterest Ads manager name',
  //   category: 'media',
  //   defaultAccountName: 'Pinterest Ads account',
  // },
  // {
  //   id: 'adform',
  //   service: 'adform',
  //   name: 'Adform',
  //   icon: 'adform',
  //   placeholder: 'Add your Adform account name',
  //   category: 'media',
  //   defaultAccountName: 'Adform account',
  //   disabled: true,
  // },
];

export const CONNECTOR_METRIC_UNITS: Record<
  string,
  Record<string, 'euro' | 'unit'>
> = {
  shopify: {
    total_sales: 'euro',
    gross_sales: 'euro',
    net_sales: 'euro',
    average_order_value: 'euro',
    orders: 'unit',
  },
  ga4: {
    sessions: 'unit',
    total_users: 'unit',
    key_events: 'unit',
    total_revenue: 'euro',
    purchase_revenue: 'euro',
  },
};

export const getDerivedKpiUnit = (
  kpiSource: string,
  kpiMetric: string,
): 'euro' | 'unit' | undefined =>
  CONNECTOR_METRIC_UNITS[kpiSource]?.[kpiMetric];

export const sourceHasDerivedUnits = (kpiSource: string): boolean =>
  kpiSource in CONNECTOR_METRIC_UNITS;

export const kpiChannelDefinitions: ChannelDefinition[] = [
  {
    id: 'google-analytics',
    service: 'ga4',
    name: 'Google Analytics 4',
    icon: 'google-analytics',
    placeholder: 'Add your GA4 property name',
    category: 'kpi',
    defaultAccountName: 'GA4 property',
  },
  {
    id: 'shopify',
    service: 'shopify',
    name: 'Shopify',
    icon: 'shopify',
    placeholder: 'Add your Shopify store',
    category: 'kpi',
    defaultAccountName: 'Shopify store',
  },
];

export interface BillingHistoryItem {
  date: string;
  description: string;
  amount: string;
  status: string;
}

export const billingHistory: BillingHistoryItem[] = [
  {
    date: 'Jul 01, 2025',
    description: 'Pro Plan - Monthly Subscription',
    amount: '$29.00',
    status: 'Paid',
  },
  {
    date: 'Jul 01, 2025',
    description: 'Pro Plan - Monthly Subscription',
    amount: '$29.00',
    status: 'Paid',
  },
  {
    date: 'Jul 01, 2025',
    description: 'Pro Plan - Monthly Subscription',
    amount: '$29.00',
    status: 'Paid',
  },
  {
    date: 'Jul 01, 2025',
    description: 'Pro Plan - Monthly Subscription',
    amount: '$29.00',
    status: 'Paid',
  },
];

export type CampaignGroup = {
  id: string;
  groupName: string;
  campaigns: number;
  created: string;
  campaignList?: Campaign[];
};

// Mock data for all available campaigns
export const allCampaigns: Campaign[] = [
  { id: '1', name: 'Summer 2025' },
  { id: '2', name: 'S.S.25' },
  { id: '3', name: 'Summer Sale Main' },
  { id: '4', name: 'Summer Sale 2024' },
  { id: '5', name: 'Summer Collection Push' },
  { id: '6', name: 'FB Summer Push' },
  { id: '7', name: 'Summer 2025 Rest' },
];

export const campaignGroups: CampaignGroup[] = [
  {
    id: '1',
    groupName: 'Summer Sale 2024 | G1',
    campaigns: 5,
    created: 'Jul 01, 2025',
    campaignList: [
      { id: '1', name: 'Summer 2025' },
      { id: '2', name: 'S.S.25' },
      { id: '3', name: 'Summer Sale Main' },
      { id: '4', name: 'FB Summer Push' },
      { id: '5', name: 'Summer 2025 Rest' },
    ],
  },
  {
    id: '2',
    groupName: 'Summer Sale 2024 | G2',
    campaigns: 3,
    created: 'Jul 01, 2025',
    campaignList: [
      { id: '1', name: 'Summer 2025' },
      { id: '2', name: 'Summer Sale Main' },
      { id: '3', name: 'Summer 2025 Rest' },
    ],
  },
  {
    id: '3',
    groupName: 'Summer Sale 2024 | G2',
    campaigns: 3,
    created: 'Jul 01, 2025',
    campaignList: [
      { id: '1', name: 'Summer 2025' },
      { id: '2', name: 'Summer Sale Main' },
      { id: '3', name: 'Summer 2025 Rest' },
    ],
  },
  {
    id: '4',
    groupName: 'Summer Sale 2024 | G2',
    campaigns: 3,
    created: 'Jul 01, 2025',
    campaignList: [
      { id: '1', name: 'Summer 2025' },
      { id: '2', name: 'Summer Sale Main' },
      { id: '3', name: 'FB Summer Push' },
    ],
  },
];
